import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  MobileHeader, Card, Alert, Badge, Spinner, SaveBar, EmptyState, ErrorState,
  CollapsibleSection, useToast, useKeyboardInset,
} from '@parsina/framework';
import { Pill, AlertTriangle } from 'lucide-react';
import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useAppNavigate } from '../../hooks/useAppNavigate.js';
import { useClinicScope } from '../../hooks/useClinicScope.jsx';
import { usePatient } from '../../hooks/usePatient.js';
import { bffQuery, bffMutation, errorCode } from '../../lib/bffClient.js';
import {
  newRxItem, dosageText, toMedicationInput, interactionIds, canSweep,
  severityKey, warningTypeKey, sortWarnings, allergenNames, conditionNames,
} from '../../lib/prescription.js';
import {
  CHECK_DRUG_INTERACTION, CREATE_MEDICATION, SWEEP_DEBOUNCE_MS,
  SEVERITY_TINT, saveLabelKey,
} from './Prescription.shared.js';
import MobileDrugSearch from '../../components/mobile/MobileDrugSearch.jsx';
import MobileRxCard from '../../components/mobile/MobileRxCard.jsx';
import { MobileSectionTitle } from '../../components/mobile/MobileKit.jsx';
import { PrescriptionSkeletonMobile } from './Prescription.skeleton.jsx';

// نسخه — the mobile task flow (a full-screen surface, no bottom nav).
//
// ┌──────────────────────────────────────┐
// │ ① MobileHeader — CONTENT mode         │  ← back to the record · «نسخه»
// │ ② Alert — allergies FIRST             │  above the fold, always
// │ ③ MobileDrugSearch                    │  a sheet, not a popover
// │ ④ MobileRxCard × N                    │  4 full-width fields, one per row
// │ ⑤ interaction panel (aria-live)       │
// │ ⑥ CollapsibleSection «داروهای قبلی»   │  collapsed
// │ [ SaveBar sticky — ثبت نسخه ]         │  docked, keyboard-aware
// └──────────────────────────────────────┘
//
// THE TWO RULES FROM THE DESKTOP SCREEN ARE UNCHANGED AND NON-NEGOTIABLE:
//
//  1. The interaction sweep NEVER blocks prescribing. It costs credits, it can
//     be exhausted, and the service fails open when the wallet is unreachable —
//     so a failed sweep is reported next to the prescription and ثبت stays live.
//     A dentist who cannot prescribe because a check timed out is a worse
//     outcome than one who prescribes without it.
//  2. The sweep is keyed on Drug.id SLUGS. Passing the Persian display name
//     reaches the rule engine as an unknown drug and returns a clean bill.
//
// Everything behavioural below — the debounce, `idsKey`, `canSweep`, the
// credits/unavailable split, `toMedicationInput`, the allSettled partial-save
// report and the idle→confirm→saving phase machine — is the desktop's, reused
// rather than re-typed (§1.1, §9).

const PrescriptionMobile = () => {
  const { patientId } = useParams();
  const [searchParams] = useSearchParams();
  const appointmentId = searchParams.get('appointmentId') || null;
  const { t, formatDate } = useTranslation();
  const navigate = useAppNavigate();
  const toast = useToast();
  const keyboardInset = useKeyboardInset();
  const { activeId } = useClinicScope();
  const { patient, loading, error, reload } = usePatient(patientId, activeId);

  const [items, setItems] = React.useState([]);
  const [phase, setPhase] = React.useState('idle');
  const [sweep, setSweep] = React.useState({ loading: false, result: null, failure: null });

  // A STRING, not an array: what the sweep depends on is *which drugs* are on
  // the prescription, and a string compares by value across renders.
  const idsKey = React.useMemo(() => interactionIds(items).join('|'), [items]);
  const sweepable = canSweep(items);

  React.useEffect(() => {
    if (!patient || !sweepable) {
      setSweep({ loading: false, result: null, failure: null });
      return undefined;
    }
    let alive = true;
    setSweep((s) => ({ ...s, loading: true }));
    const timer = setTimeout(() => {
      bffQuery(CHECK_DRUG_INTERACTION, {
        // Rebuilt from the key rather than closed over `ids`: the memoised array
        // gets a new identity on every keystroke in a dose field, which would
        // re-fire this effect — and re-bill the sweep — for an edit that is not
        // even an input to it.
        medicationIds: idsKey.split('|').filter(Boolean),
        patientId: patient.id,
        patientConditions: conditionNames(patient),
        patientAllergies: allergenNames(patient),
      })
        .then((data) => {
          if (!alive) return;
          setSweep({ loading: false, result: data?.checkDrugInteraction || null, failure: null });
        })
        .catch((err) => {
          if (!alive) return;
          const code = errorCode(err);
          setSweep({
            loading: false,
            result: null,
            failure: code === 'INSUFFICIENT_CREDITS' ? 'credits' : 'unavailable',
          });
        });
    }, SWEEP_DEBOUNCE_MS);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [idsKey, patient, sweepable]);

  const addDrug = (drug) => {
    setItems((prev) => (prev.some((i) => i.drug.id === drug.id) ? prev : [...prev, newRxItem(drug)]));
    setPhase('idle');
  };
  const updateItem = (next) => {
    setItems((prev) => prev.map((i) => (i.key === next.key ? next : i)));
    setPhase('idle');
  };
  const removeItem = (key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
    setPhase('idle');
  };

  const save = async () => {
    // 'idle' → 'confirm' → 'saving'. A prescription is a clinical write; it
    // takes two deliberate taps, not one stray one — and a phone is where a
    // stray tap is most likely.
    if (phase === 'idle') {
      setPhase('confirm');
      return;
    }
    if (phase !== 'confirm') return;
    setPhase('saving');

    // One mutation per row. allSettled rather than a chain: a formulary row the
    // service rejects must not silently discard the rows after it, and the
    // dentist has to be told exactly how many landed.
    const results = await Promise.allSettled(
      items.map((item) =>
        bffMutation(CREATE_MEDICATION, {
          patientId: patient.id,
          input: toMedicationInput(item, { appointmentId }),
        }),
      ),
    );
    const saved = results.filter((r) => r.status === 'fulfilled').length;

    if (saved === items.length) {
      toast.success(t('prescription.saved'));
      navigate('patient', { id: patient.id, tab: 'history' });
      return;
    }
    if (saved === 0) toast.error(t('prescription.saveFailed'));
    else toast.warn(t('prescription.savePartial', { saved, total: items.length }));
    setPhase('idle');
  };

  // The task's OWN mobile shape — the gradient band, the search field, the
  // empty prescription, the interactions card and the docked commit row —
  // never a generic card stack (DESIGN.md §"Loading & skeleton").
  if (loading && !patient) {
    return <PrescriptionSkeletonMobile label={t('common.loading')} />;
  }

  if (!patient) {
    return (
      <div data-testid="rx-content" data-state="error" role="alert" style={{ padding: '0.875rem 0.75rem' }}>
        <ErrorState
          title={t('patient.error.title')}
          description={error ? t('errors.load.patient') : t('patient.notFound.description')}
          onRetry={error ? reload : undefined}
          retryLabel={t('common.actions.retry')}
        />
      </div>
    );
  }

  const allergies = patient.allergies || [];
  const warnings = sortWarnings(sweep.result?.warnings);
  const history = patient.medications || [];

  return (
    <div
      data-testid="rx-content"
      data-state={items.length ? 'content' : 'empty'}
      style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
    >
      {/* ONE scroll region, with the header inside it: this is a task flow, and
          the task — not the chrome — owns the viewport once the dentist starts
          typing. The commit row below is the only thing that stays. */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <MobileHeader
          onBack={() => navigate('patient', { id: patient.id })}
          backLabel={t('common.actions.back')}
          title={t('prescription.title')}
          subtitle={t('prescription.subtitle', { name: patient.name })}
          data-testid="rx-header"
        />

        <div style={{ padding: '0.875rem 0.75rem 0' }}>
        {/* ② Allergies lead. On the desktop they sit in a right rail the eye
            reaches after the drug list; on a phone there is no rail, and the one
            fact that changes what may be prescribed goes above the fold. */}
        {allergies.length > 0 && (
          <Alert
            variant="warning"
            icon={<AlertTriangle size={16} />}
            title={t('prescription.allergies.title')}
            style={{ marginBottom: '0.875rem' }}
          >
            {allergies.map((a) => a.allergen).filter(Boolean).join(' · ')}
          </Alert>
        )}

        <MobileDrugSearch onAdd={addDrug} />

        <div data-testid="rx-items" data-state={items.length ? 'content' : 'empty'} style={{ marginTop: '0.875rem' }}>
          {items.length === 0 ? (
            <Card>
              <EmptyState
                icon={<Pill size={44} color={C.textMuted} />}
                title={t('prescription.empty.title')}
                description={t('prescription.empty.description')}
              />
            </Card>
          ) : (
            items.map((item) => (
              <MobileRxCard
                key={item.key}
                item={item}
                onChange={updateItem}
                onRemove={() => removeItem(item.key)}
                testId={`rx-item-${item.key}`}
              />
            ))
          )}
        </div>

        {/* ⑤ The one region whose content arrives BY ITSELF, a second or two
            after a drug is added, while the dentist is typing a dose somewhere
            else entirely. Without a live region a screen reader never mentions
            that a high-severity interaction appeared — and a silently-delivered
            interaction warning is the same as no interaction warning. Polite,
            not assertive: it must not interrupt mid-word, and ثبت is never
            blocked either way. */}
        <section
          data-testid="rx-interactions"
          data-state={
            !sweepable ? 'idle'
              : sweep.loading ? 'loading'
                : sweep.failure ? 'error'
                  : warnings.length ? 'content' : 'empty'
          }
          aria-live="polite"
          aria-busy={sweep.loading || undefined}
          style={{ marginTop: '0.875rem' }}
        >
          <MobileSectionTitle>{t('prescription.interactions.title')}</MobileSectionTitle>
          <Card style={{ padding: '0.875rem' }}>
            {!sweepable && (
              <div style={{ fontSize: '0.75rem', color: C.textMuted }}>
                {t('prescription.interactions.needsTwo')}
              </div>
            )}

            {sweepable && sweep.loading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Spinner size={16} />
                <span style={{ fontSize: '0.75rem', color: C.textMuted }}>
                  {t('prescription.interactions.checking')}
                </span>
              </div>
            )}

            {sweepable && !sweep.loading && sweep.failure === 'credits' && (
              <Alert variant="info" title={t('errors.credits.title')}>
                {t('errors.credits.body')}
              </Alert>
            )}

            {sweepable && !sweep.loading && sweep.failure === 'unavailable' && (
              <div style={{ fontSize: '0.75rem', color: C.textMuted }}>
                {t('prescription.interactions.unavailable')}
              </div>
            )}

            {sweepable && !sweep.loading && !sweep.failure && warnings.length === 0 && (
              <div style={{ fontSize: '0.75rem', color: CT.goodInk }}>
                {t('prescription.interactions.none')}
              </div>
            )}

            {warnings.map((w, i) => {
              const sev = severityKey(w.severity);
              const tint = SEVERITY_TINT[sev];
              const typeKey = warningTypeKey(w.warningType);
              return (
                <div
                  key={`${w.drugPair || w.drug || 'w'}-${i}`}
                  style={{
                    background: tint.bg,
                    color: tint.ink,
                    borderRadius: '0.625rem',
                    padding: '0.625rem 0.75rem',
                    marginBottom: '0.5rem',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: '0.6875rem', fontWeight: 800 }}>
                      {t(`prescription.interactions.severity.${sev}`)}
                    </strong>
                    {typeKey && (
                      <span style={{ fontSize: '0.6875rem' }}>
                        · {t(`prescription.interactions.type.${typeKey}`)}
                      </span>
                    )}
                    {w.drugPair && (
                      <span
                        dir="ltr"
                        style={{ unicodeBidi: 'isolate', fontSize: '0.6875rem', marginInlineStart: 'auto' }}
                      >
                        {w.drugPair}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.75rem', lineHeight: 1.8, marginTop: '0.25rem' }}>
                    {w.description}
                  </div>
                </div>
              );
            })}
          </Card>
        </section>

        {/* ⑥ History is context, not the task — collapsed, so the fold belongs
            to what is being prescribed now. */}
        <div style={{ marginTop: '0.875rem' }}>
          <CollapsibleSection title={t('prescription.history.title')}>
            {history.length === 0 ? (
              <div style={{ fontSize: '0.75rem', color: C.textMuted, padding: '0.25rem 0 0.5rem' }}>
                {t('prescription.history.empty')}
              </div>
            ) : (
              history.slice(0, 8).map((m) => (
                <div key={m.id} style={{ padding: '0.375rem 0' }}>
                  <div dir="auto" style={{ fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary }}>
                    {m.display}
                  </div>
                  {m.authoredOn && (
                    <div style={{ fontSize: '0.6875rem', color: C.textMuted }}>
                      {t('patient.history.prescribedOn', { date: formatDate(m.authoredOn, 'numeric') })}
                    </div>
                  )}
                </div>
              ))
            )}
          </CollapsibleSection>
        </div>

        {items.length > 0 && (
          <Card style={{ marginTop: '0.875rem', padding: '0.75rem' }}>
            <div style={{ fontSize: '0.6875rem', color: C.textMuted, lineHeight: 1.9 }}>
              {items.map((item) => (
                <div key={item.key} style={{ marginBottom: '0.25rem' }}>
                  <Badge variant="outline">{item.drug.name}</Badge>{' '}
                  <span dir="auto">{dosageText(item)}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

          <div style={{ height: '1rem' }} />
        </div>
      </div>

      {/* §6: "submit docks bottom". `useKeyboardInset` exists because dvh/svh
          and `position: fixed` all describe the LAYOUT viewport and are blind to
          the iOS software keyboard — without it the commit row ends up under
          the keys exactly while a dose field is focused. */}
      <SaveBar
        sticky
        onSave={save}
        saving={phase === 'saving'}
        disabled={items.length === 0}
        saveLabel={t(saveLabelKey(phase))}
        data-testid="rx-savebar"
        style={{
          marginTop: 0,
          flexShrink: 0,
          paddingInline: '0.75rem',
          paddingBottom: `calc(0.75rem + env(safe-area-inset-bottom, 0px) + ${keyboardInset}px)`,
        }}
      />
    </div>
  );
};

export default PrescriptionMobile;
